import { useMemo } from "react";
import { MessageSquare, Mail, Phone, Globe, Send, StickyNote } from "lucide-react";
import { cn } from "@/lib/utils";
import type { Contact } from "@/lib/types";

/**
 * Returns the list of channels we can reach a contact on, plus an icon
 * and a "ready" flag (whether they have the corresponding identity).
 */
export interface ChannelOption {
  channel: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  ready: boolean;
  hint?: string;
}

// Tiny inline SVGs for social channels — Lucide v1 dropped most brand icons.
const InstagramIcon = (p: { className?: string }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" {...p}>
    <rect x="2" y="2" width="20" height="20" rx="5" /><circle cx="12" cy="12" r="4" /><circle cx="17.5" cy="6.5" r="0.6" fill="currentColor" />
  </svg>
);
const FacebookIcon = (p: { className?: string }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" {...p}>
    <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" />
  </svg>
);
const LinkedinIcon = (p: { className?: string }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" {...p}>
    <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-4 0v7h-4v-7a6 6 0 0 1 6-6z" /><rect x="2" y="9" width="4" height="12" /><circle cx="4" cy="4" r="2" />
  </svg>
);
const TwitterIcon = (p: { className?: string }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...p}>
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zM17.083 19.77h1.833L7.084 4.126H5.117z" />
  </svg>
);
const TikTokIcon = (p: { className?: string }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...p}>
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V8.36a8.16 8.16 0 0 0 4.77 1.52V6.43a4.85 4.85 0 0 1-1.84-.74z" />
  </svg>
);
const YouTubeIcon = (p: { className?: string }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...p}>
    <path d="M23 7.5a3 3 0 0 0-2.1-2.1C19.07 5 12 5 12 5s-7.07 0-8.9.4A3 3 0 0 0 1 7.5 31 31 0 0 0 .5 12 31 31 0 0 0 1 16.5a3 3 0 0 0 2.1 2.1C4.93 19 12 19 12 19s7.07 0 8.9-.4a3 3 0 0 0 2.1-2.1A31 31 0 0 0 23.5 12 31 31 0 0 0 23 7.5zM10 15V9l5 3z" />
  </svg>
);
const WhatsAppIcon = (p: { className?: string }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...p}>
    <path d="M17.6 6.32A7.85 7.85 0 0 0 12.05 4 7.94 7.94 0 0 0 5.13 15.87L4 20l4.25-1.11a7.93 7.93 0 0 0 3.8 1h.01A7.92 7.92 0 0 0 20 12.05a7.86 7.86 0 0 0-2.4-5.73zm-5.55 12.2A6.6 6.6 0 0 1 8.6 17.6l-.24-.15-2.52.66.67-2.46-.16-.25a6.6 6.6 0 0 1 5.7-9.96 6.59 6.59 0 0 1 4.66 1.93 6.55 6.55 0 0 1 1.93 4.67 6.6 6.6 0 0 1-6.59 6.59zm3.62-4.94c-.2-.1-1.17-.58-1.35-.65-.18-.07-.31-.1-.45.1-.13.2-.51.65-.63.78-.12.13-.23.15-.43.05a5.4 5.4 0 0 1-1.6-.99 6 6 0 0 1-1.1-1.37c-.12-.2-.01-.31.09-.41.09-.09.2-.23.3-.35.1-.12.13-.2.2-.33.06-.13.03-.25-.02-.35-.05-.1-.45-1.08-.62-1.48-.16-.39-.33-.34-.45-.34a.86.86 0 0 0-.4-.02.78.78 0 0 0-.56.26 2.34 2.34 0 0 0-.74 1.74c0 1.03.75 2.02.85 2.16.1.13 1.47 2.25 3.57 3.16 1.96.84 1.96.56 2.31.53.36-.04 1.16-.48 1.32-.94.16-.46.16-.85.11-.94-.05-.08-.18-.13-.38-.23z" />
  </svg>
);

/** iMessage: a speech bubble (filled, like the Messages app icon). */
export const IMessageIcon = (p: { className?: string }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...p}>
    <path d="M12 3C6.48 3 2 6.92 2 11.75c0 2.66 1.37 5.05 3.53 6.66-.15 1.1-.62 2.37-1.6 3.4 2.1-.12 3.86-.93 4.98-1.73.99.27 2.03.42 3.09.42 5.52 0 10-3.92 10-8.75S17.52 3 12 3z" />
  </svg>
);

export function getContactChannels(contact: Contact): ChannelOption[] {
  const socials = contact.socials ?? {};
  return [
    { channel: "email",     label: "Email",     icon: Mail,          ready: !!contact.email,         hint: contact.email ?? "No email on file" },
    { channel: "sms",       label: "SMS",       icon: MessageSquare, ready: !!contact.phone,         hint: contact.phone ?? "No phone on file" },
    { channel: "voice",     label: "Voice",     icon: Phone,         ready: !!contact.phone,         hint: contact.phone ?? "No phone on file" },
    // Sent from the workspace's iMessage number through its iMessage Mac
    // (SyncSuite Desktop). Reaches a phone number or an Apple ID email.
    { channel: "imessage",  label: "iMessage",  icon: IMessageIcon,  ready: !!(contact.phone || contact.email), hint: contact.phone || contact.email || "No phone or email on file" },
    { channel: "whatsapp",  label: "WhatsApp",  icon: WhatsAppIcon,  ready: !!(socials.whatsapp || contact.phone), hint: socials.whatsapp || contact.phone || "No WhatsApp on file" },
    { channel: "instagram", label: "Instagram", icon: InstagramIcon, ready: !!socials.instagram,     hint: socials.instagram ?? "No Instagram on file" },
    { channel: "facebook",  label: "Facebook",  icon: FacebookIcon,  ready: !!socials.facebook,      hint: socials.facebook ?? "No Facebook on file" },
    { channel: "linkedin",  label: "LinkedIn",  icon: LinkedinIcon,  ready: !!socials.linkedin,      hint: socials.linkedin ?? "No LinkedIn on file" },
    { channel: "twitter",   label: "Twitter / X", icon: TwitterIcon, ready: !!socials.twitter,       hint: socials.twitter ?? "No Twitter on file" },
    { channel: "telegram",  label: "Telegram",  icon: Send,          ready: !!socials.telegram,      hint: socials.telegram ?? "No Telegram on file" },
    { channel: "tiktok",    label: "TikTok",    icon: TikTokIcon,    ready: !!socials.tiktok,        hint: socials.tiktok ?? "No TikTok on file" },
    { channel: "youtube",   label: "YouTube",   icon: YouTubeIcon,   ready: !!socials.youtube,       hint: socials.youtube ?? "No YouTube on file" },
    // Internal note isn't an outbound channel — it just stores a private
    // message visible only to the team. Always available regardless of
    // contact identities.
    { channel: "internal_note", label: "Internal note", icon: StickyNote, ready: true, hint: "Private note, never sent to the contact" },
  ];
}

export function ChannelIcon({ channel, className }: { channel: string; className?: string }) {
  const map: Record<string, React.ComponentType<{ className?: string }>> = {
    sms:       MessageSquare,
    voice:     Phone,
    imessage:  IMessageIcon,
    email:     Mail,
    whatsapp:  WhatsAppIcon,
    instagram: InstagramIcon,
    facebook:  FacebookIcon,
    linkedin:  LinkedinIcon,
    twitter:   TwitterIcon,
    telegram:  Send,
    tiktok:    TikTokIcon,
    youtube:   YouTubeIcon,
    internal_note: StickyNote,
  };
  const Icon = map[channel] ?? Globe;
  return <Icon className={cn("h-3 w-3", className)} />;
}

/**
 * A row of clickable channel chips. Picks highlight the active channel.
 * Channels the contact doesn't have an identity for are hidden behind a
 * "more" hint to keep the UI compact.
 */
export function ChannelChips({
  contact,
  selected,
  onSelect,
}: {
  contact: Contact;
  selected: string | null;
  onSelect: (channel: string) => void;
}) {
  const channels = useMemo(() => getContactChannels(contact), [contact]);
  const ready = channels.filter((c) => c.ready);
  const unavailable = channels.filter((c) => !c.ready);

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {ready.map((c) => (
        <button
          key={c.channel}
          type="button"
          onClick={() => onSelect(c.channel)}
          title={c.hint}
          className={cn(
            "inline-flex items-center gap-1 rounded-md px-2 h-7 text-xs font-medium transition-colors",
            selected === c.channel
              ? "bg-indigo-500 text-white"
              : "bg-zinc-800 text-zinc-300 hover:bg-zinc-700"
          )}
        >
          <c.icon className="h-3 w-3" />
          {c.label}
        </button>
      ))}
      {unavailable.length > 0 && (
        <span className="text-[10px] text-zinc-600 ml-1">
          {unavailable.length} more channel{unavailable.length === 1 ? "" : "s"} need contact info
        </span>
      )}
    </div>
  );
}
